import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useGetQuoteItemsQuery, useUpdateQuoteItemMutation } from '@/app/services/quoteItems'
import { selectLocale } from '@/app/slices/appSlice'
import {
    selectCurrencyCode,
    selectOrganisationId,
    selectUseImperialUnits,
    selectUseTubeV2,
} from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { formatNumber } from '@/common/helpers/formatUtilities'
import {
    useMutationErrorSnackbar,
    useNumberFormatter,
    useQuoteItemPropertyOptions,
    useToolBoxTreatments,
} from '@/common/hooks'
import { getCurrencyFormat, getPartDimensions } from '@/common/utils'
import {
    getPartDimensionsDisplay,
    getRecordDimensionsDisplay,
    getThicknessDisplay,
    getThicknessOptionValue,
    PROFILE_TYPES,
} from '@/common/utils/SheetUtils/SheetUtils'

const PartSettings = ({ quoteItemId }) => {
    const { t } = useTranslation()
    const notifyMutationError = useMutationErrorSnackbar()
    const { quoteId } = useParams()

    const { n } = useNumberFormatter()
    const { showGrainDirection } = useToolBoxTreatments()

    const organisationId = useSelector(selectOrganisationId)
    const showTubeOptions = useSelector(selectUseTubeV2)
    const locale = useSelector(selectLocale)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const currencyCode = useSelector(selectCurrencyCode)

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const [updateQuoteItem] = useUpdateQuoteItemMutation()

    const quoteItemProfile = quoteItem.profile
    const quoteItemDimensions = useMemo(() => {
        return getPartDimensionsDisplay(quoteItem, n)
    }, [quoteItem?.profile, quoteItem?.diameter, quoteItem?.rotaryProfileWidth, quoteItem?.rotaryProfileLength, n])

    const quoteItemSheetProfile = useMemo(() => {
        if (!showTubeOptions || !quoteItem?.sheetId) return null

        return quoteItem?.sheet?.profile || null
    }, [quoteItem?.sheetId, showTubeOptions])

    const quoteItemSheetDimensions = useMemo(() => {
        if (!showTubeOptions || !quoteItem?.sheetId) return null

        return getRecordDimensionsDisplay(quoteItem?.sheet, n)
    }, [quoteItem?.sheetId, showTubeOptions, quoteItem?.sheet, n])

    const [itemCuttingTechnology, setItemCuttingTechnology] = useState(quoteItem?.cuttingTechnologyId || '')
    const [itemMaterial, setItemMaterial] = useState(quoteItem?.materialId || '')
    const [itemProfile, setItemProfile] = useState(quoteItemSheetProfile || quoteItemProfile || '')
    const [itemProfileDimensions, setItemProfileDimensions] = useState(
        quoteItemSheetDimensions || quoteItemDimensions || ''
    )
    const [itemThickness, setItemThickness] = useState(quoteItem?.thickness || '')
    const [itemWebThickness, setItemWebThickness] = useState(quoteItem?.webThickness || '')
    const [itemSheet, setItemSheet] = useState(quoteItem?.sheetId || '')
    const [itemConsumptionMode, setItemConsumptionMode] = useState(quoteItem?.materialConsumptionMode || '')
    const [itemIsCustomerSuppliedMaterial, setItemIsCustomerSuppliedMaterial] = useState(
        quoteItem?.isCustomerSuppliedMaterial || false
    )
    const [itemQuantity, setItemQuantity] = useState(quoteItem?.quantity || 1)
    const [quantityError, setQuantityError] = useState(false)

    const partDimensions = useMemo(() => getPartDimensions(quoteItem), [quoteItem])
    const units = useMemo(() => (useImperialUnits ? 'in' : 'mm'), [useImperialUnits])
    const mode = useMemo(() => (quoteItem && 'profile' in quoteItem ? 'rotary' : 'flat'), [quoteItem])

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)

    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const handleUpdateQuoteItem = async (quoteItem) => {
        try {
            await updateQuoteItem({
                organisationId,
                quoteId,
                quoteItemId: quoteItem?.id,
                quoteItem,
            }).unwrap()
        } catch (error) {
            notifyMutationError(error, t('Failed to update the $t(quote) item'))
        }
    }

    const debounceHandleUpdateQuoteItem = useDebouncedCallback(handleUpdateQuoteItem, 800)

    const {
        cuttingTechOptions,
        findMatchingProfileDimension,
        grainDirectionOptions,
        materialOptions,
        profileDimensions,
        profileOptions,
        sheetOptions,
        thicknessOptions,
    } = useQuoteItemPropertyOptions({
        cuttingTechnologyId: itemCuttingTechnology,
        materialId: itemMaterial,
        thickness: itemThickness,
        webThickness: itemWebThickness || null,
        profileValue: itemProfile,
        profileDimensionsValue: itemProfileDimensions,
        mode,
        partDimensions,
    })

    // QM-3717: composite (webThickness, thickness) value for the thickness Select — see
    // getThicknessOptionValue. A no-op string(thickness) for every profile but IBeam.
    const thicknessSelectValue = useMemo(
        () =>
            getThicknessOptionValue({ profile: itemProfile, thickness: itemThickness, webThickness: itemWebThickness }),
        [itemProfile, itemThickness, itemWebThickness]
    )

    // The web / flange caption under the Thickness label shows only when the visible options use
    // the "web/flange" format (getThicknessDisplay), so non-IBeam profiles keep a plain label.
    const hasIBeamThicknessOption = thicknessOptions?.some((option) => option.profile === PROFILE_TYPES.I_BEAM)

    // Enhanced memoization for sheetOptions with better dependency tracking
    const memoizedSheetOptions = useMemo(() => {
        if (!sheetOptions?.length) return []
        return sheetOptions
    }, [
        sheetOptions?.length,
        sheetOptions?.map((option) => `${option.sheetId}-${option.profile}-${option.sheetSize}`).join(',') || '',
    ])

    const handleCuttingTechChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemCuttingTechnology !== value) {
            setItemCuttingTechnology(value)
            setItemMaterial('')
            setItemProfile('')
            setItemProfileDimensions('')
            setItemThickness('')
            setItemWebThickness('')
            setItemSheet('')
            setItemConsumptionMode('')
            setItemIsCustomerSuppliedMaterial(false)

            await handleUpdateQuoteItem({
                ...quoteItem,
                cuttingTechnologyId: value,
                materialId: '',
                thickness: '',
                sheetId: '',
                sheet: null,
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
                webThickness: '',
            })
        }
    }

    const handleMaterialChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemMaterial !== value) {
            setItemMaterial(value)
            setItemProfile(quoteItemSheetProfile || quoteItemProfile || '')
            setItemProfileDimensions('')
            setItemThickness('')
            setItemWebThickness('')
            setItemSheet('')
            setItemConsumptionMode('')
            setItemIsCustomerSuppliedMaterial(false)
            await handleUpdateQuoteItem({
                ...quoteItem,
                materialId: value,
                thickness: '',
                sheetId: '',
                sheet: null,
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
                webThickness: '',
            })
        }
    }

    const handleProfileChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemProfile !== value) {
            setItemProfile(value)
            setItemProfileDimensions('')
            setItemThickness('')
            setItemWebThickness('')
            setItemSheet('')
            setItemConsumptionMode('')
            setItemIsCustomerSuppliedMaterial(false)
        }
    }

    const handleProfileDimensionsChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemProfileDimensions !== value) {
            setItemProfileDimensions(value)
            setItemThickness('')
            setItemWebThickness('')
            setItemSheet('')
            setItemConsumptionMode('')
            setItemIsCustomerSuppliedMaterial(false)
        }
    }

    const handleThicknessChange = async (event) => {
        const value = event?.target?.value
        if (!value || value === thicknessSelectValue) return

        // Resolve the composite Select value back to its (thickness, webThickness) pair —
        // event.target.value must never be written to itemThickness directly (QM-3717).
        const option = thicknessOptions?.find((o) => o.value === value)
        if (!option) return

        setItemThickness(option.thickness)
        setItemWebThickness(option.webThickness || '')
        setItemSheet('')
        setItemConsumptionMode('')
        setItemIsCustomerSuppliedMaterial(false)
    }

    const handleSheetChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemSheet !== value) {
            const selectedSheet = sheetOptions?.find((option) => option.sheetId === value) || {}
            const { defaultMaterialConsumptionMode, isSoldByOrganisation, profile, sheetSize, webThickness } =
                selectedSheet

            setItemSheet(value)
            setItemProfile(profile)
            setItemProfileDimensions(sheetSize)
            setItemConsumptionMode(defaultMaterialConsumptionMode)
            setItemIsCustomerSuppliedMaterial(!isSoldByOrganisation)
            await handleUpdateQuoteItem({
                ...quoteItem,
                sheetId: value,
                sheet: selectedSheet,
                materialConsumptionMode: defaultMaterialConsumptionMode,
                isCustomerSuppliedMaterial: !isSoldByOrganisation,
                webThickness: webThickness || '',
            })
        }
    }

    const handleGrainDirectionChange = async (event) => {
        const value = event?.target?.value
        if (!value || quoteItem?.grainDirection === value) return

        await handleUpdateQuoteItem({
            ...quoteItem,
            grainDirection: value,
        })
    }

    const handleQuantityChange = async (values) => {
        debounceHandleUpdateQuoteItem.cancel()

        const { floatValue } = values
        setQuantityError(!floatValue || isNaN(floatValue) || floatValue <= 0)

        setItemQuantity(floatValue)

        if (!isNaN(floatValue) && floatValue >= 1) {
            debounceHandleUpdateQuoteItem({
                ...quoteItem,
                quantity: floatValue,
            })
        }
    }

    const loadDefaultSheetOption = async () => {
        if (sheetOptions?.length && itemThickness && !itemSheet) {
            const firstSheet = sheetOptions[0]

            const { defaultMaterialConsumptionMode, isSoldByOrganisation, profile, sheetId, sheetSize, webThickness } =
                firstSheet

            if (itemSheet === sheetId) return

            setItemSheet(sheetId)
            setItemProfile(profile)
            setItemProfileDimensions(sheetSize)
            setItemConsumptionMode(defaultMaterialConsumptionMode || '')
            setItemIsCustomerSuppliedMaterial(!isSoldByOrganisation)

            if (
                quoteItem.thickness !== itemThickness ||
                quoteItem.sheetId !== sheetId ||
                quoteItem.materialConsumptionMode !== defaultMaterialConsumptionMode ||
                quoteItem.isCustomerSuppliedMaterial !== !isSoldByOrganisation
            ) {
                await handleUpdateQuoteItem({
                    ...quoteItem,
                    thickness: itemThickness,
                    sheetId: sheetId,
                    sheet: firstSheet,
                    materialConsumptionMode: defaultMaterialConsumptionMode,
                    isCustomerSuppliedMaterial: !isSoldByOrganisation,
                    webThickness: webThickness || '',
                })
            }
        }
    }

    useEffect(() => {
        setItemQuantity(quoteItem.quantity)
    }, [quoteItem?.quantity])

    useEffect(() => {
        setItemCuttingTechnology(quoteItem?.cuttingTechnologyId || '')
    }, [quoteItem?.cuttingTechnologyId])

    useEffect(() => {
        setItemMaterial(quoteItem?.materialId || '')
    }, [quoteItem?.materialId])

    useEffect(() => {
        setItemProfile(quoteItemSheetProfile || quoteItemProfile || '')
    }, [quoteItemSheetProfile, quoteItemProfile])

    useEffect(() => {
        setItemProfileDimensions(quoteItemSheetDimensions || quoteItemDimensions || '')
    }, [quoteItemSheetDimensions, quoteItemDimensions])

    useEffect(() => {
        setItemThickness(quoteItem?.thickness || '')
    }, [quoteItem?.thickness])

    useEffect(() => {
        setItemWebThickness(quoteItem?.webThickness || '')
    }, [quoteItem?.webThickness])

    useEffect(() => {
        setItemSheet(quoteItem?.sheetId || '')
    }, [quoteItem?.sheetId])

    useEffect(() => {
        setItemConsumptionMode(quoteItem?.materialConsumptionMode || '')
    }, [quoteItem?.materialConsumptionMode])

    useEffect(() => {
        setItemIsCustomerSuppliedMaterial(quoteItem?.isCustomerSuppliedMaterial || false)
    }, [quoteItem?.isCustomerSuppliedMaterial])

    useEffect(() => {
        loadDefaultSheetOption()
    }, [memoizedSheetOptions])

    useEffect(() => {
        if (!showTubeOptions || !itemProfile || !quoteItemProfile || !quoteItemDimensions) return

        if (!profileDimensions?.length) {
            if (itemProfileDimensions) {
                setItemProfileDimensions('')
            }
            return
        }

        if (itemProfileDimensions && profileDimensions.includes(itemProfileDimensions)) {
            return
        }

        const matchingDimension = findMatchingProfileDimension(quoteItemDimensions, profileDimensions)
        if (matchingDimension && profileDimensions.includes(matchingDimension)) {
            setItemProfileDimensions(matchingDimension)
            return
        }
        setItemProfileDimensions('')
    }, [
        itemProfile,
        profileDimensions,
        findMatchingProfileDimension,
        quoteItemDimensions,
        itemProfileDimensions,
        itemSheet,
        quoteItemSheetDimensions,
    ])

    return (
        <Box key={quoteItemId}>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Quantity')}</Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <NumericFormat
                        allowNegative={false}
                        customInput={TextField}
                        decimalSeparator={currencyFormat.decimal}
                        error={quantityError}
                        helperText={quantityError ? t('Quantity cannot be less than 1') : null}
                        inputProps={{
                            style: { textAlign: 'right' },
                            'data-testid': 'nqp-quote-item-quantity',
                        }}
                        placeholder="0"
                        size="small"
                        thousandSeparator={currencyFormat.group}
                        value={itemQuantity}
                        variant="outlined"
                        onValueChange={handleQuantityChange}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Cutting technology')}</Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Select
                            disabled={!cuttingTechOptions?.length || isPartFromLibrary}
                            id="select-itemCuttingTechnology"
                            inputProps={{ 'data-testid': 'nqp-quote-item-cutting-technology' }}
                            labelId="select-itemCuttingTechnology-label"
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            size="small"
                            value={itemCuttingTechnology}
                            fullWidth
                            onChange={handleCuttingTechChange}
                        >
                            {cuttingTechOptions
                                ? cuttingTechOptions?.map((ct) => (
                                      <MenuItem
                                          key={ct.cuttingTechnologyId}
                                          value={ct.cuttingTechnologyId}
                                      >
                                          {ct.name}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Material')}</Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 3,
                        }}
                    >
                        <Select
                            disabled={isPartFromLibrary}
                            id="select-itemMaterial"
                            inputProps={{ 'data-testid': 'nqp-quote-item-material' }}
                            labelId="select-itemMaterial-label"
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            size="small"
                            value={itemMaterial}
                            fullWidth
                            onChange={handleMaterialChange}
                        >
                            {materialOptions
                                ? materialOptions?.map((material) => (
                                      <MenuItem
                                          key={material.materialId}
                                          value={material.materialId}
                                      >
                                          {material.materialName}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>
            {mode === 'rotary' && showTubeOptions ? (
                <>
                    <Box
                        alignItems={'center'}
                        display={'flex'}
                        flexDirection={'row'}
                        gap={1}
                        justifyContent={'space-between'}
                        margin={1}
                    >
                        <Box flex={1}>
                            <Typography>{t('Profile type')}</Typography>
                        </Box>
                        <TbxTooltip
                            title={
                                isPartFromLibrary
                                    ? itemMaterial == '' || itemThickness == ''
                                        ? t('Material for Part Library part is no longer available for selection')
                                        : t('Cannot edit parts from library')
                                    : ''
                            }
                            arrow
                        >
                            <Box flex={1}>
                                <Select
                                    disabled={
                                        !itemCuttingTechnology ||
                                        !itemMaterial ||
                                        !profileOptions?.length ||
                                        isPartFromLibrary
                                    }
                                    id="select-itemProfile"
                                    inputProps={{ 'data-testid': 'nqp-quote-item-profile' }}
                                    labelId="select-itemProfile-label"
                                    SelectDisplayProps={{ style: { textAlign: 'right' } }}
                                    size="small"
                                    value={itemProfile}
                                    fullWidth
                                    onChange={handleProfileChange}
                                >
                                    {profileOptions
                                        ? profileOptions?.map((profile) => (
                                              <MenuItem
                                                  key={profile}
                                                  value={profile}
                                              >
                                                  {profile}
                                              </MenuItem>
                                          ))
                                        : null}
                                </Select>
                            </Box>
                        </TbxTooltip>
                    </Box>
                    <Box
                        alignItems={'center'}
                        display={'flex'}
                        flexDirection={'row'}
                        gap={1}
                        justifyContent={'space-between'}
                        margin={1}
                    >
                        <Box flex={1}>
                            <Typography>{t('Profile dimensions')}</Typography>
                        </Box>
                        <TbxTooltip
                            title={
                                isPartFromLibrary
                                    ? itemMaterial == '' || itemThickness == ''
                                        ? t('Material for Part Library part is no longer available for selection')
                                        : t('Cannot edit parts from library')
                                    : ''
                            }
                            arrow
                        >
                            <Box flex={1}>
                                <Select
                                    disabled={!itemProfile || !profileDimensions?.length || isPartFromLibrary}
                                    id="select-itemProfileDimensions"
                                    inputProps={{ 'data-testid': 'nqp-quote-item-profile-dimensions' }}
                                    labelId="select-itemProfileDimensions-label"
                                    SelectDisplayProps={{ style: { textAlign: 'right' } }}
                                    size="small"
                                    value={itemProfileDimensions}
                                    fullWidth
                                    onChange={handleProfileDimensionsChange}
                                >
                                    {profileDimensions
                                        ? profileDimensions?.map((dimension) => (
                                              <MenuItem
                                                  key={dimension}
                                                  value={dimension}
                                              >
                                                  {dimension} {units}
                                              </MenuItem>
                                          ))
                                        : null}
                                </Select>
                            </Box>
                        </TbxTooltip>
                    </Box>
                </>
            ) : null}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Typography>{t('Thickness')}</Typography>
                    {hasIBeamThicknessOption ? (
                        <Typography
                            sx={{ color: 'text.secondary' }}
                            variant="caption"
                        >
                            {t('web / flange')}
                        </Typography>
                    ) : null}
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Select
                            disabled={
                                !itemMaterial ||
                                !thicknessOptions?.length ||
                                (mode === 'rotary' && showTubeOptions && (!itemProfile || !itemProfileDimensions)) ||
                                isPartFromLibrary
                            }
                            id="select-itemThickness"
                            inputProps={{ 'data-testid': 'nqp-quote-item-thickness' }}
                            labelId="select-itemThickness-label"
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            size="small"
                            value={thicknessSelectValue}
                            fullWidth
                            onChange={handleThicknessChange}
                        >
                            {thicknessOptions
                                ? thicknessOptions?.map((option) => (
                                      <MenuItem
                                          key={option.value}
                                          sx={{ justifyContent: 'flex-end' }}
                                          value={option.value}
                                      >
                                          {getThicknessDisplay(option, (value) =>
                                              formatNumber(value, locale, useImperialUnits)
                                          )}{' '}
                                          {units}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Sheet')}</Typography>
                </Box>
                <Box
                    sx={{
                        flex: 3,
                    }}
                >
                    <Select
                        disabled={!itemThickness || !sheetOptions || !sheetOptions?.length}
                        id="select-itemSheet"
                        inputProps={{ 'data-testid': 'nqp-quote-item-sheet-size' }}
                        labelId="select-itemSheet-label"
                        SelectDisplayProps={{ style: { textAlign: 'right' } }}
                        size="small"
                        value={itemSheet}
                        fullWidth
                        onChange={handleSheetChange}
                    >
                        {sheetOptions
                            ? sheetOptions?.map((sheet) => (
                                  <MenuItem
                                      key={sheet.sheetId}
                                      sx={{ justifyContent: 'flex-end' }}
                                      value={sheet.sheetId}
                                  >
                                      {quoteItem && 'profile' in quoteItem
                                          ? `${formatNumber(sheet.materialLength, locale)} ${units}`
                                          : `${formatNumber(sheet.sheetWidth, locale)} x ${formatNumber(
                                                sheet.sheetHeight,
                                                locale
                                            )} ${units}`}
                                  </MenuItem>
                              ))
                            : null}
                    </Select>
                </Box>
            </Box>
            {showGrainDirection && mode === 'flat' ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'space-between',
                        margin: 1,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Typography>{t('Grain direction')}</Typography>
                    </Box>
                    <TbxTooltip
                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                        arrow
                    >
                        <Box
                            sx={{
                                flex: 1,
                            }}
                        >
                            <Select
                                disabled={isPartFromLibrary}
                                id="select-itemGrainDirection"
                                inputProps={{ 'data-testid': 'nqp-quote-item-grain-direction' }}
                                labelId="select-itemGrainDirection-label"
                                SelectDisplayProps={{ style: { textAlign: 'right' } }}
                                size="small"
                                value={quoteItem?.grainDirection || 'None'}
                                fullWidth
                                onChange={handleGrainDirectionChange}
                            >
                                {grainDirectionOptions.map((direction) => (
                                    <MenuItem
                                        key={direction.value}
                                        value={direction.value}
                                    >
                                        {direction.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </TbxTooltip>
                </Box>
            ) : null}
        </Box>
    )
}

PartSettings.propTypes = {
    isPartFromLibrary: PropTypes.bool,
    quoteItemId: PropTypes.string,
}

export default PartSettings
